import type {
  CorrectedCatalogCardElement,
  DataTableElement,
  DecisionTwoPathCardElement,
  DriveFileCardElement,
  KpiStripElement,
  PatternCardElement,
  TextBlockElement,
} from 'src/demo-mode/demo-elements/types'
import type { DemoChatScript } from 'src/demo-mode/types'

import {
  ABSORBED,
  BILLING,
  BILLING_MODEL,
  CATEGORY_DOLLARS,
  CENSUS,
  DEFECT_CATEGORIES,
  DRAFT,
  FIX_LIST,
  FIX_LIST_ROWS,
  HAZMAT,
  HERO_SKU,
  INTAKE_AUDITOR,
  PATHS,
  ROOT_CAUSE,
  WORLD,
} from '../data'

const n = (x: number) => x.toLocaleString('en-US')
const usd = (x: number) => `$${x.toLocaleString('en-US')}`
const k = (x: number) => `$${Math.round(x / 1_000)}K`

// ─── Drive-discovered files (Neo finds → user confirms → Neo uses) ──────────

/** Beat 1: the SKU master Neo finds in Drive. Confirm gates the census. */
const CATALOG_FILE: DriveFileCardElement = {
  kind: 'drive-file-card',
  id: 'sku-act1-catalog-file',
  fileName: 'Cascade SKU Master Catalog',
  fileType: 'sheet',
  path: 'Ops › Catalog',
  updatedLabel: 'Updated 2 days ago',
  owner: 'Priya Nair · Catalog Ops',
  sizeLabel: `${n(CENSUS.skusScanned)} rows`,
  confirmProposalId: 'demo-sku-master-data-auditor-use-catalog',
}

/** Beat 2: the ShipHero shipping/billing export Neo finds in Drive. Confirm
 *  gates the dollars join. */
const SHIPPING_FILE: DriveFileCardElement = {
  kind: 'drive-file-card',
  id: 'sku-act2-shipping-file',
  fileName: 'Cascade Q2 Shipping Export',
  fileType: 'sheet',
  path: 'Ops › Shipping',
  updatedLabel: 'Updated yesterday',
  owner: 'Marcus Chen · Billing Ops',
  sizeLabel: `${n(BILLING.adjustmentLines)} rows`,
  confirmProposalId: 'demo-sku-master-data-auditor-use-shipping',
}

// ─────────────────────────── Beat 1 — the census ───────────────────────────

const CENSUS_KPIS: KpiStripElement = {
  kind: 'kpi-strip',
  id: 'sku-act1-census-kpis',
  tiles: [
    {
      label: 'SKUs scanned',
      value: n(CENSUS.skusScanned),
      sublabel: `across ${WORLD.brands} brands`,
    },
    {
      label: 'Flagged',
      value: `${n(CENSUS.flagged)} (${CENSUS.flaggedPct}%)`,
      sublabel: 'defects in 4 categories',
      status: 'warning',
    },
    {
      label: 'Shipped in last 90 days',
      value: n(CENSUS.shippedIn90d),
      sublabel: 'of the flagged SKUs',
    },
  ],
}

const CENSUS_TABLE: DataTableElement = {
  kind: 'data-table',
  id: 'sku-act1-census-table',
  subtitle: `${n(CENSUS.skusScanned)} SKUs validated · catalog export only`,
  columns: [
    { key: 'category', label: 'Defect category' },
    { key: 'skus', label: 'SKUs', align: 'right' },
    { key: 'shipped', label: 'Shipped 90d', align: 'right' },
  ],
  rows: DEFECT_CATEGORIES.map((c) => ({
    id: c.id,
    status: c.status,
    cells: {
      category: c.label,
      skus: n(c.skuCount),
      shipped: n(c.shipped90d),
    },
  })),
  takeaway: `${CENSUS.dormantPct}% of flagged SKUs are dormant, so the raw count overstates it. The cost sits in the ones that ship.`,
}

const HAZMAT_CALLOUT: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act1-hazmat',
  text: `**${HAZMAT.skuCount} SKUs carry an un-flagged hazmat signature** (${HAZMAT.lithium} lithium, ${HAZMAT.aerosol} aerosol, ${HAZMAT.flammable} flammable) — DOT/IATA and carrier-suspension exposure, not a dollar line. Every one goes to human review.`,
}

const HERO_DRILL: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act1-hero-drill',
  text: `One example: ${HERO_SKU.sku} — a "${HERO_SKU.titleLiters}L" duffel whose master dims ${HERO_SKU.masterDims} at ${HERO_SKU.masterWeight} compute to ~${HERO_SKU.masterLiters}L. Placeholder, and it repeats across dozens of SKUs from one import. **Want the dollars? I'll pull your Q2 shipping actuals from Drive.**`,
}

// ─────────────────────────── Beat 2 — the dollars ──────────────────────────

const DOLLARS_KPIS: KpiStripElement = {
  kind: 'kpi-strip',
  id: 'sku-act2-dollars-kpis',
  tiles: [
    {
      label: 'Absorbed last 90 days',
      value: k(ABSORBED.total90d),
      sublabel: 'rated off bad catalog data',
      status: 'critical',
    },
    {
      label: 'Run-rate',
      value: `~${k(ABSORBED.monthlyRunRate)}/mo`,
      sublabel: `~${k(ABSORBED.annualized)}/yr`,
      status: 'warning',
    },
    {
      label: `Top ${FIX_LIST.headSkuCount} SKUs`,
      value: k(FIX_LIST.headDollars),
      sublabel: `${FIX_LIST.headSharePct}% of all rating deltas`,
      status: 'critical',
    },
  ],
}

const RANK_FLIP_TABLE: DataTableElement = {
  kind: 'data-table',
  id: 'sku-act2-rankflip',
  subtitle: 'Same defects — ranked by dollars, not count',
  columns: [
    { key: 'category', label: 'Defect category' },
    { key: 'skus', label: 'SKUs', align: 'right' },
    { key: 'dollars', label: '90-day absorbed', align: 'right' },
  ],
  rows: CATEGORY_DOLLARS.map((c) => ({
    id: c.id,
    status: c.status,
    emphasize: c.id === 'dims',
    cells: {
      category: c.label,
      skus: n(c.skuCount),
      dollars: c.dollars90d === null ? '—' : k(c.dollars90d),
    },
  })),
  takeaway: `The most common defect isn't the expensive one. Placeholder dims (${n(1_460)} SKUs) cost ${k(130_000)} — 3.3× what missing weights cost (${n(3_720)} SKUs, ${k(39_000)}), from 2.5× fewer SKUs.`,
}

const FIX_LIST_TABLE: DataTableElement = {
  kind: 'data-table',
  id: 'sku-act2-fixlist',
  subtitle: `Dollar-ranked fix list · top ${FIX_LIST_ROWS.length} of ${FIX_LIST.headSkuCount}`,
  columns: [
    { key: 'sku', label: 'SKU · brand' },
    { key: 'defect', label: 'Defect' },
    { key: 'units', label: 'Units 90d', align: 'right' },
    { key: 'absorbed', label: 'Absorbed', align: 'right' },
  ],
  rows: FIX_LIST_ROWS.map((r) => ({
    id: r.sku,
    status: r.emphasize ? 'critical' : 'warning',
    emphasize: r.emphasize,
    cells: {
      sku: `${r.sku} · ${r.brand}`,
      defect: r.defect,
      units: n(r.units90d),
      absorbed: usd(r.absorbed90d),
    },
  })),
  takeaway: `Fix ${FIX_LIST.headSkuCount} SKUs and you stop ${FIX_LIST.headSharePct}% of the rating deltas — ${k(FIX_LIST.headDollars)} across the head. Ranked by dollars, because ranking by error count puts the wrong SKUs first.`,
}

const HERO_MATH: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act2-hero-math',
  text: `Row 1 is that duffel. Carrier measured ${HERO_SKU.measuredDims} / ${HERO_SKU.measuredWeight} → ~${usd(HERO_SKU.absorbedPerParcel)} absorbed per parcel × ${HERO_SKU.reDimmed90d} shipments = **${usd(HERO_SKU.absorbed90d)} on one SKU in 90 days.** The correct dims were in your own billing export.`,
}

const BILLING_MODEL_FINDING: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act2-billing-model',
  text: `The gap lands on Cascade's P&L because your rate cards quote off master data while the carrier bills on actuals. **${BILLING_MODEL.ownDataSharePct}% of these adjustments trace to your own bad SKU data** — nothing to dispute, only to fix.`,
}

const ROOT_CAUSE_CARD: PatternCardElement = {
  kind: 'pattern-card',
  id: 'sku-act2-rootcause',
  tag: 'Root-cause pattern',
  title: 'Placeholder dims trace to a handful of catalog imports',
  stats: [
    {
      label: 'Share of placeholder dims',
      value: `${ROOT_CAUSE.importSharePct}%`,
      emphasize: true,
    },
    {
      label: 'Source',
      value: `${ROOT_CAUSE.importCount} Feb–Apr catalog imports`,
    },
    {
      label: 'Largest single source',
      value: ROOT_CAUSE.largestSource,
      emphasize: true,
    },
    { label: 'Fix', value: 'Validate templates at import' },
  ],
  affected: [
    {
      facility: ROOT_CAUSE.largestImport,
      status: 'orange',
      note: 'Seasonal line refresh for an existing brand. Blank template fields defaulted to placeholder dims and got copied across the whole spring line.',
    },
  ],
  takeaway: `One template-validation rule at import beats ${n(1_460)} hand edits.`,
}

const DOLLARS_CUE: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act2-cue',
  text: `${k(ABSORBED.annualized)} a year, and ${FIX_LIST.headSkuCount} rows carry ${FIX_LIST.headSharePct}% of it. The corrected values are already computed. **Want the corrected catalog drafted?**`,
}

// ─────────────────────────── Beat 3 — the fix ──────────────────────────────

const CORRECTED_DRAFT: CorrectedCatalogCardElement = {
  kind: 'corrected-catalog-card',
  id: 'sku-act3-draft',
  fileName: DRAFT.fileName,
  headerLabel: `${n(CENSUS.flagged)} flagged rows highlighted · summary tab by category`,
  rows: [
    {
      sku: HERO_SKU.sku,
      brand: HERO_SKU.brand,
      field: 'Dimensions',
      current: `${HERO_SKU.masterDims} · ${HERO_SKU.masterWeight}`,
      proposed: `${HERO_SKU.measuredDims} · ${HERO_SKU.measuredWeight}`,
      confidence: 'high',
    },
    {
      sku: 'ALT-TUB-0662',
      brand: 'Altitude Supplements',
      field: 'Weight',
      current: '1.0 lb (default)',
      proposed: '3.4 lb',
      confidence: 'medium',
    },
    {
      sku: 'OAK-SRM-0912',
      brand: 'OakHill Skincare',
      field: 'HS code',
      current: '— (blank)',
      proposed: '3304.99 — proposed',
      confidence: 'review',
    },
  ],
  sources: [
    {
      label: 'Carrier-billed actuals',
      count: DRAFT.fromActuals,
      confidence: 'high',
      note: 'The carrier measured these — SKUs that shipped in the 90-day window.',
    },
    {
      label: 'Variant / plausibility',
      count: DRAFT.fromInference,
      confidence: 'medium',
      note: 'Inferred from variant siblings and catalog plausibility.',
    },
    {
      label: 'Needs re-measure',
      count: DRAFT.needsRemeasure,
      confidence: 'review',
      note: 'No source to pull from. Flagged for cubiscan instead of guessed.',
    },
  ],
  footer: `HS-code proposals are marked "proposed — broker review required"; the ${HAZMAT.skuCount} hazmat rows are flagged for human review and never auto-filled. Nothing is written to ShipHero — this is a reviewable Google Sheet in your Drive.`,
}

const DRAFT_INTRO: TextBlockElement = {
  kind: 'text-block',
  id: 'sku-act3-intro',
  text: `Here's the corrected catalog — a Google Sheet in your Drive. Every flagged row is highlighted and labeled with its source, so your team knows what to trust and what to re-measure.`,
}

const ACT3_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'after-approvals-sku-act3-closer',
  text: `Once the fixes go in, the ~${k(ABSORBED.monthlyRunRate)}/mo stops — but new SKUs land every week. **Want this running on every one of them?**`,
}

// ─────────────────────────── Beat 4 — keep it fixed ────────────────────────

const DECISION: DecisionTwoPathCardElement = {
  kind: 'decision-two-path-card',
  id: 'sku-act4-decision',
  title: 'Two ways forward',
  paths: [
    {
      label: 'Fix once',
      impact: `~${k(PATHS.stoppedAnnual)}/yr stopped now`,
      impactTone: 'warning',
      lines: [
        `But ~${n(PATHS.newSkusPerMonth)} new/changed SKUs land per month at the ${PATHS.historicalDefectRatePct}% historical defect rate — ~${PATHS.newDefectsPerMonth} defective SKUs a month.`,
        `The leak regrows to ~${k(PATHS.regrownMonthlyByYearEnd)}/mo within a year — ~${k(PATHS.reabsorbedYearOne)} re-absorbed in year one, climbing.`,
      ],
    },
    {
      label: 'Fix + SKU Intake Auditor',
      impact: `~${k(PATHS.stoppedAnnual)}/yr — and it stays stopped`,
      impactTone: 'good',
      recommended: true,
      lines: [
        'Same fix now, plus every new and changed SKU validated at import — brand onboarding and existing-brand line refreshes alike.',
        'The leak stays plugged; the catalog team gets a weekly exceptions sheet.',
      ],
    },
  ],
}

const ACT4_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'after-approvals-sku-act4-closer',
  text: `Approve and the SKU Intake Auditor goes live in the Agents tab — weekly and on every import, exceptions to ${INTAKE_AUDITOR.deliveryChannel}. Two files, one afternoon: priced, ranked, drafted.`,
}

export const skuMasterDataAudit: DemoChatScript = {
  id: 'sku-master-data-audit',
  name: 'SKU Master Data Auditor',
  description:
    'Single flow, four beats: Neo finds the SKU catalog in Drive → you confirm → census of 6,088 defects (catalog-only); Neo finds the Q2 shipping export in Drive → you confirm → $189K/90d absorbed, the count-vs-dollar rank flip, and the dollar-ranked fix list; corrected-catalog Google Sheet + hazmat review; two-path decision → stand up the standing SKU Intake Auditor. Zero integration — two files already in the team’s Drive.',
  triggers: [
    'sku',
    'master data',
    'catalog export',
    'audit our catalog',
    'check our skus',
    'check our catalog',
    'missing weights',
    'bad dims',
    'placeholder dims',
    'hs code',
    'hazmat',
    'catalog clean',
    'clean our catalog',
    'dims and weights',
    'product data',
  ],
  events: [
    // ─────────── Beat 1a: Neo finds the catalog in Drive ───────────
    { type: 'status', delayMs: 200, message: 'Searching your Drive…' },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Looking for your product catalog — the SKU master with dims, weights, HS codes, and hazmat flags. Checking the Ops shared drive first.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'search_drive' },
    { type: 'text-delta', delayMs: 500, delta: 'Found your catalog.' },
    { type: 'demo-element-add', delayMs: 600, element: CATALOG_FILE },
    { type: 'done', delayMs: 400, title: 'SKU audit — found the catalog' },
    // Confirm gate: "Use this file" on CATALOG_FILE dispatches
    // `neo-demo-approval-resolved` for this proposalId, which resumes the
    // census below (same wiring an approval card uses).
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-sku-master-data-auditor-use-catalog',
    },

    // ─────────── Beat 1b: the census (on confirm) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: `Parsing ${n(CENSUS.skusScanned)} SKUs across ${WORLD.brands} brands…`,
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Running dims plausibility — density outliers, 1×1×1 defaults, and identical dims repeated across unrelated SKUs. Then a weight null/zero scan, an HS-code format check on the SKUs that shipped internationally, and hazmat keyword signatures: lithium, aerosol, flammable.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'parse_catalog_export' },
    { type: 'tool-call', delayMs: 300, name: 'validate_master_data' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `Validated ${n(CENSUS.skusScanned)} SKUs. ${n(CENSUS.flagged)} carry a master-data defect — ${CENSUS.flaggedPct}% of the catalog.`,
    },
    { type: 'demo-element-add', delayMs: 600, element: CENSUS_KPIS },
    { type: 'demo-element-add', delayMs: 700, element: CENSUS_TABLE },
    { type: 'demo-element-add', delayMs: 700, element: HAZMAT_CALLOUT },
    { type: 'demo-element-add', delayMs: 700, element: HERO_DRILL },
    { type: 'done', delayMs: 400, title: 'SKU audit — the census' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2a: Neo finds the shipping export in Drive ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Finding your shipping actuals…',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Looking for the shipping export your team pulls from ShipHero each quarter — per-parcel carrier adjustments with order and SKU references. Checking the Ops shared drive.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'search_drive' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Found your Q2 shipping export.',
    },
    { type: 'demo-element-add', delayMs: 600, element: SHIPPING_FILE },
    {
      type: 'done',
      delayMs: 400,
      title: 'SKU audit — found the shipping export',
    },
    // Confirm gate: "Use this file" on SHIPPING_FILE resumes the dollars join.
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-sku-master-data-auditor-use-shipping',
    },

    // ─────────── Beat 2b: the dollars (on confirm) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: `Joining 90 days of carrier adjustments to the flagged SKUs…`,
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta: `Matching on the ShipHero export's order-reference field. Pricing only single-SKU shipments — ~${BILLING.singleSkuSharePct}% of adjusted parcels — where the SKU is unambiguous. The multi-SKU parcels are left unpriced, so this is a conservative floor.\n`,
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'join_billing_export' },
    { type: 'tool-call', delayMs: 300, name: 'price_defects' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `${n(BILLING.linesToFlagged)} of ${n(BILLING.adjustmentLines)} carrier-adjustment lines trace back to the flagged SKUs.`,
    },
    { type: 'demo-element-add', delayMs: 600, element: DOLLARS_KPIS },
    { type: 'demo-element-add', delayMs: 700, element: RANK_FLIP_TABLE },
    { type: 'demo-element-add', delayMs: 700, element: FIX_LIST_TABLE },
    { type: 'demo-element-add', delayMs: 700, element: HERO_MATH },
    { type: 'demo-element-add', delayMs: 700, element: BILLING_MODEL_FINDING },
    { type: 'demo-element-add', delayMs: 700, element: ROOT_CAUSE_CARD },
    { type: 'demo-element-add', delayMs: 700, element: DOLLARS_CUE },
    { type: 'done', delayMs: 400, title: 'SKU audit — the dollars' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 3: the fix (corrected catalog draft) ───────────
    { type: 'tool-call', delayMs: 300, name: 'draft_corrected_catalog' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `Drafted the corrected catalog — ${n(CENSUS.flagged)} flagged rows, current and proposed side by side.`,
    },
    { type: 'demo-element-add', delayMs: 600, element: DRAFT_INTRO },
    { type: 'demo-element-add', delayMs: 700, element: CORRECTED_DRAFT },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-sku-master-data-auditor-act1-send-draft',
        toolName: 'send_corrected_catalog_draft',
        actionType: 'file_share',
        description: `Share the corrected-catalog Google Sheet "${DRAFT.fileName}" (with the by-category summary tab) via Drive with the catalog lead and the four FC inventory-control leads for review. Shared via Drive — nothing written to ShipHero.`,
      },
    },
    {
      type: 'approval',
      delayMs: 500,
      proposal: {
        proposalId: 'demo-sku-master-data-auditor-act1-hazmat-review',
        toolName: 'open_hazmat_review_tasks',
        actionType: 'review_tasks',
        description: `Open ${HAZMAT.skuCount} hazmat-review tasks with the compliance owner — ${HAZMAT.lithium} lithium, ${HAZMAT.aerosol} aerosol, ${HAZMAT.flammable} flammable. Nothing auto-reclassified; each needs human sign-off.`,
      },
    },
    { type: 'demo-element-add', delayMs: 700, element: ACT3_CLOSER },
    { type: 'done', delayMs: 400, title: 'SKU audit — corrected draft' },
    // Beat 3→4 advances on a TYPED reply to the ACT3_CLOSER question ("Want
    // this running…?"), not on resolving an approval. The two Beat-3 approval
    // cards above stay independently resolvable; they're no longer the gate.
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 4: keep it fixed (agent-ization) ───────────
    {
      type: 'text-delta',
      delayMs: 400,
      delta: 'Two ways to run this from here.',
    },
    { type: 'demo-element-add', delayMs: 600, element: DECISION },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-sku-master-data-auditor-act1-intake-auditor',
        toolName: 'create_sku_intake_auditor',
        actionType: 'agent_creation',
        description: `Stand up the standing SKU Intake Auditor — runs weekly and on every catalog import (brand onboarding and existing-brand line refreshes), validating new and changed SKUs. Exceptions to the catalog team; hazmat suspects to compliance.`,
      },
    },
    { type: 'demo-element-add', delayMs: 700, element: ACT4_CLOSER },
    { type: 'done', delayMs: 400, title: 'SKU audit — always on' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-sku-master-data-auditor-act1-intake-auditor',
    },
  ],
}
